const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js';
const TOKEN_TIMEOUT_MS = 30_000;
const READY_POLL_MS = 50;

type TurnstileTheme = 'auto' | 'light' | 'dark';

interface TurnstileRenderOptions {
  sitekey: string;
  theme?: TurnstileTheme;
  size?: 'normal' | 'compact' | 'flexible';
  execution?: 'render' | 'execute';
  appearance?: 'always' | 'execute' | 'interaction-only';
  callback?: (token: string) => void;
  'error-callback'?: (error?: unknown) => void;
  'expired-callback'?: () => void;
}

interface TurnstileApi {
  render: (
    container: HTMLElement | string,
    options: TurnstileRenderOptions
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
  execute?: (widgetId?: string, options?: { action?: string }) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

class TurnstileManager {
  private scriptPromise: Promise<void> | null = null;
  private widgetId: string | null = null;
  private container: HTMLElement | null = null;
  private rendering: Promise<void> | null = null;
  private tokenConsumed = false;
  private pendingResolvers: Array<(token: string) => void> = [];
  private pendingRejecters: Array<(reason: Error) => void> = [];

  ensureScript(): Promise<void> {
    if (window.turnstile) return Promise.resolve();
    if (this.scriptPromise) return this.scriptPromise;

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${TURNSTILE_SCRIPT_SRC}"]`
      );
      if (existing) {
        if (window.turnstile) {
          resolve();
        } else {
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener(
            'error',
            () => reject(new Error('Failed to load Turnstile script')),
            { once: true }
          );
        }
        return;
      }

      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.scriptPromise = null;
        reject(new Error('Failed to load Turnstile script'));
      };
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  render(container: HTMLElement): Promise<void> {
    if (this.rendering) return this.rendering;
    this.rendering = this.doRender(container).catch(err => {
      this.rendering = null;
      throw err;
    });
    return this.rendering;
  }

  private async doRender(container: HTMLElement): Promise<void> {
    if (!SITE_KEY) {
      console.warn('VITE_TURNSTILE_SITE_KEY is not set; Turnstile disabled');
      return;
    }
    await this.ensureScript();
    if (!window.turnstile) {
      throw new Error(
        'Turnstile script loaded but window.turnstile is missing'
      );
    }

    if (this.widgetId && this.container === container) return;

    if (this.widgetId) this.remove();

    this.container = container;
    this.widgetId = window.turnstile.render(container, {
      sitekey: SITE_KEY,
      theme: 'auto',
      appearance: 'interaction-only',
      callback: token => this.resolvePending(token),
      'error-callback': () =>
        this.rejectPending(new Error('Turnstile verification failed')),
      'expired-callback': () => {
        this.tokenConsumed = true;
      },
    });
  }

  async getToken(): Promise<string> {
    if (!SITE_KEY) return '';

    await this.waitUntilReady(TOKEN_TIMEOUT_MS);

    const turnstile = window.turnstile;
    const widgetId = this.widgetId;
    if (!turnstile || !widgetId) {
      throw new Error('Turnstile widget is not ready');
    }

    return this.requestToken(turnstile, widgetId, 1);
  }

  private requestToken(
    turnstile: TurnstileApi,
    widgetId: string,
    retriesLeft: number
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const existingToken = turnstile.getResponse(widgetId);
      if (existingToken && !this.tokenConsumed) {
        this.tokenConsumed = true;
        resolve(existingToken);
        return;
      }

      let settled = false;

      const timeout = setTimeout(() => {
        if (settled) return;

        settled = true;
        this.dropResolver(wrappedResolve, wrappedReject);

        if (retriesLeft > 0) {
          this.tokenConsumed = false;
          turnstile.reset(widgetId);
          this.requestToken(turnstile, widgetId, retriesLeft - 1).then(
            resolve,
            reject
          );
          return;
        }

        reject(new Error('Turnstile token request timed out'));
      }, TOKEN_TIMEOUT_MS);

      const wrappedResolve = (token: string) => {
        if (settled) return;

        settled = true;
        clearTimeout(timeout);

        this.tokenConsumed = true;
        resolve(token);
      };

      const wrappedReject = (reason: Error) => {
        if (settled) return;

        settled = true;
        clearTimeout(timeout);

        reject(reason);
      };

      this.pendingResolvers.push(wrappedResolve);
      this.pendingRejecters.push(wrappedReject);

      if (this.tokenConsumed) {
        this.tokenConsumed = false;
        turnstile.reset(widgetId);
      }
    });
  }

  private waitUntilReady(timeoutMs: number): Promise<void> {
    if (window.turnstile && this.widgetId) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (window.turnstile && this.widgetId) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() - start > timeoutMs) {
          clearInterval(interval);
          reject(new Error('Turnstile widget is not ready'));
        }
      }, READY_POLL_MS);
    });
  }

  getTokenSync(): string {
    if (!SITE_KEY || !window.turnstile || !this.widgetId) return '';
    return window.turnstile.getResponse(this.widgetId) ?? '';
  }

  reset(): void {
    if (window.turnstile && this.widgetId) {
      window.turnstile.reset(this.widgetId);
    }
  }

  remove(): void {
    if (window.turnstile && this.widgetId) {
      window.turnstile.remove(this.widgetId);
    }
    this.widgetId = null;
    this.container = null;
    this.rendering = null;
    this.tokenConsumed = false;
  }

  private resolvePending(token: string) {
    const resolvers = this.pendingResolvers;
    this.pendingResolvers = [];
    this.pendingRejecters = [];
    resolvers.forEach(resolve => resolve(token));
  }

  private rejectPending(reason: Error) {
    const rejecters = this.pendingRejecters;
    this.pendingResolvers = [];
    this.pendingRejecters = [];
    rejecters.forEach(reject => reject(reason));
  }

  private dropResolver(
    resolve: (token: string) => void,
    reject: (reason: Error) => void
  ) {
    this.pendingResolvers = this.pendingResolvers.filter(r => r !== resolve);
    this.pendingRejecters = this.pendingRejecters.filter(r => r !== reject);
  }
}

export const turnstileManager = new TurnstileManager();
export type { TurnstileApi, TurnstileRenderOptions };
